import React, { useState } from 'react';
import { Card, CardContent, Typography, Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import ComponentHeaderDescriptions from '../ComponentHeaderDescriptions';
import ComponentHeaderTitles from '../ComponentHeaderTitles';
import { useEffect } from 'react';

/**
 * @function MultipleEanSelection
 * @description Renders the multiple ean selection component. This is rendered if multipleEanCodesForProducts is not empty.
 * The user must select one of the possible EAN codes for each product. This value is saved in the setSelectedEan function.
 * This results in an object looking like this:
 * {
 *     RequiredGasMeterEAN: '1234567890123',
 *    RequiredStroomMeterEAN: '1234567890123'
 * }
 * 
 * @chosenProduct {Array} chosenProduct - An array of chosen products (e.g. "Electriciteit", "Gas"). This is used to determine which meters to show.
 * @setSelectedEan {Function} setSelectedEan - A function to set the selectedEan state. Updates the state in the parent component.
 * @multipleEanCodesForProducts {Object} multipleEanCodesForProducts - An object containing the EAN codes for each product. Should look like this:
 * {
 *    GAS: ['1234567890123', '1234567890123'],
 *   ELK: ['1234567890123', '1234567890123']
 * }
 * although it doesn't neccesarily have to contain both GAS and ELK.
 * 
 * 
 * @returns {JSX.Element} The rendered component.
 */
const MultipleEanSelection = ({
    chosenProduct,
    setSelectedEan,
    multipleEanCodesForProducts,
    setAnyRelevantMultipleEanCodes
}) => {

    const initialState = {
        RequiredGasMeterEAN: '',
        RequiredStroomMeterEAN: ''
    };

    const [localSelectedEan, setLocalSelectedEan] = useState(initialState);

    // Reset the selectedEan state on mount
    useEffect(() => {
        setSelectedEan(initialState);
        setLocalSelectedEan(initialState);
        // used by the parent state to determine whether to skip this step. 
        setAnyRelevantMultipleEanCodes(true);
    }, []);


    /**
     * Called when a card is clicked. Updates the localSelectedEan and selectedEan states.
     * @param {*} ean Value of the clicked card
     * @param {*} productType Key of the clicked card. Can be either 'RequiredGasMeterEAN' or 'RequiredStroomMeterEAN'
     */
    const handleCardClick = (ean, productType) => {
        const newSelectedEan = { ...localSelectedEan, [productType]: ean };
        setLocalSelectedEan(newSelectedEan);
        setSelectedEan(newSelectedEan);
    };

    /**
     * Called when a radio button is clicked. Updates the localSelectedEan and selectedEan states.
     * 
     * @param {*} event 
     */
    const handleEanSelection = (event) => {
        const { value, name } = event.target;
        setLocalSelectedEan({ ...localSelectedEan, [name]: value });
        setSelectedEan({ ...localSelectedEan, [name]: value });
    };

    /**
     * Create an object containing the meters to display for each product.
     * Checks if the chosenProduct array contains the product. If so, add the EAN codes for that product to the object.
     * otherwise, add an empty array.
     * @type {Object}
     */
    let metersToDisplay = {
        RequiredGasMeterEAN: chosenProduct.includes('Gas') ? multipleEanCodesForProducts.Gas || [] : [],
        RequiredStroomMeterEAN: chosenProduct.includes('Electriciteit') ? multipleEanCodesForProducts.ELK || [] : []
    };





    /**
     * Returns an user-friendly title for the productType.
     * @param {*} productType 
     * @returns 
     */
    const getTitle = (productType) => {
        productType = productType.replace('Required', '');
        productType = productType.replace('MeterEAN', '');
        productType = productType + ' meter';
        return productType;
    };
    return (
        <div className="multiple-ean-select">
            <FormControl component="fieldset">
                {Object.entries(metersToDisplay).map(([productType, meters]) => (
                    <React.Fragment key={productType}>
                        {meters.length > 0 && (
                            <section>
                                <ComponentHeaderTitles title={`Selecteer uw ${getTitle(productType)}`} />
                                <ComponentHeaderDescriptions description="Wij hebben meerdere EAN meters gevonden op uw adres. Zou u uw EAN meter willen selecteren?" />
                                <RadioGroup
                                    name={productType}
                                    value={localSelectedEan[productType]}
                                    onChange={handleEanSelection}
                                >
                                    {meters.map((meter) => (
                                        <Card key={meter} onClick={() => handleCardClick(meter, productType)}>                                            <CardContent>
                                            <FormControlLabel
                                                value={meter}
                                                control={<Radio />}
                                                label={<Typography variant="h5">{meter}</Typography>}
                                            />
                                        </CardContent>
                                        </Card>
                                    ))}
                                </RadioGroup>
                            </section>
                        )}
                    </React.Fragment>
                ))}
                 {/*  We basically shouldn't get here due to the state management of setAnyRelevantMultipleEanCodes. So if we got here, something has gone wrong. */}
                {chosenProduct.length > 0 && Object.values(metersToDisplay).every(arr => arr.length === 0) && (
                    <Typography variant="subtitle1">
                        Er is helaas iets mis gegaan. Neem contact op met de klantenservice als je niet verder komt.
                    </Typography>
                )}
            </FormControl>
        </div>
    );
};

MultipleEanSelection.displayName = "MultipleEanSelection";
export default MultipleEanSelection;
